import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Container, Row, Col, Badge, Card } from 'react-bootstrap';
import axios from 'axios';
import { APP_STRUCTURE } from '../PermissionConfig'; // Import the config we made
import NavigationBar from '../NavigationBar';

function ManageUsers() {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Form State
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('user');
    // Paths this user is BLOCKED from. Everything not in this list is allowed.
    const [selectedRestricted, setSelectedRestricted] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/users`, { withCredentials: true });
            setUsers(res.data);
        } catch (err) {
            console.error("Error fetching users", err);
        }
    };

    const handleShow = (user = null) => {
        if (user) {
            setEditingUser(user);
            setUsername(user.Username);
            setRole(user.Role);
            setPassword(''); // Don't show hash
            // Parse restricted pages if it comes as string from DB, otherwise use as is
            let restricted = user.RestrictedPages;
            if (typeof restricted === 'string') restricted = JSON.parse(restricted);
            setSelectedRestricted(restricted || []);
        } else {
            // New User Defaults: nothing restricted = full access
            setEditingUser(null);
            setUsername('');
            setPassword('');
            setRole('user');
            setSelectedRestricted([]);
        }
        setShowModal(true);
    };

    const handleSave = async () => {
        const payload = { username, password, role, restrictedPages: selectedRestricted };
        try {
            if (editingUser) {
                await axios.put(`${window.backendUrl}/users/${editingUser.UserID}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${window.backendUrl}/users`, payload, { withCredentials: true });
            }
            setShowModal(false);
            fetchUsers();
        } catch (err) {
            alert("Error saving user");
        }
    };


    const getItemPaths = (item) => {
        const paths = item.extraPaths || [];
        if (item.path) paths.push(item.path);
        return paths;
    };

    // "Checked" means this category is fully RESTRICTED (blocked) for the user
    const isCategoryChecked = (categoryItems) => {
        return categoryItems.every(item => {
            const allItemPaths = getItemPaths(item);
            if (allItemPaths.length === 0) return true;
            return allItemPaths.every(p => selectedRestricted.includes(p));
        });
    };

    const toggleCategory = (categoryItems) => {
        const allCategoryPaths = categoryItems.flatMap(item => getItemPaths(item));
        if (isCategoryChecked(categoryItems)) {
            setSelectedRestricted(prev => prev.filter(p => !allCategoryPaths.includes(p)));
        } else {
            setSelectedRestricted(prev => [...new Set([...prev, ...allCategoryPaths])]);
        }
    };

    const togglePath = (item) => {
        const pathsToToggle = getItemPaths(item);


        const isAlreadyRestricted = pathsToToggle.length > 0 &&
                                  pathsToToggle.every(p => selectedRestricted.includes(p));

        if (isAlreadyRestricted) {
            setSelectedRestricted(prev => prev.filter(p => !pathsToToggle.includes(p)));
        } else {
            setSelectedRestricted(prev => [...new Set([...prev, ...pathsToToggle])]);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            await axios.delete(`${window.backendUrl}/users/${userToDelete.UserID}`, { withCredentials: true });
            setShowDeleteModal(false);
            setUserToDelete(null);
            fetchUsers(); // Refresh the table
        } catch (err) {
            console.error("Error deleting user", err);
            alert("Failed to delete user");
        }
    };

    return (
        <>
            <NavigationBar/>
            <Container className="mt-4">
                <div className="d-flex justify-content-between mb-3">
                    <h3>User Management</h3>
                    <Button variant="primary" onClick={() => handleShow(null)}>Create New User</Button>
                </div>

                <Card className="shadow-sm">
                    <Table hover responsive>
                        <thead className="bg-light text-center"> {/* Added text-center */}
                            <tr>
                                <th>S.L</th> {/* Changed "ID" to "S.L" */}
                                <th>Username</th>
                                <th>Role</th>
                                <th>Access Level</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Added 'index' parameter here */}
                            {users.map((u, index) => (
                                <tr key={u.UserID} className="text-center"> {/* Added text-center */}
                                    {/* Display Serial Number (index + 1) instead of u.UserID */}
                                    <td>{index + 1}</td>
                                    <td>{u.Username}</td>
                                    <td>
                                        <Badge bg={u.Role === 'admin' ? 'danger' : 'success'}>
                                            {u.Role.toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td>
                                        {u.Role === 'admin' ? 'Full Access' : (() => {
                                            const restricted = typeof u.RestrictedPages === 'string' ? JSON.parse(u.RestrictedPages) : u.RestrictedPages;
                                            const count = restricted?.length || 0;
                                            return count === 0 ? 'Full Access' : `${count} Page${count > 1 ? 's' : ''} Restricted`;
                                        })()}
                                    </td>
                                    <td>
                                        <Button size="sm" variant="outline-primary" onClick={() => handleShow(u)} className="me-2">
                                            Edit / Access
                                        </Button>
                                        {/* NEW DELETE BUTTON */}
                                        {u.Role !== 'admin' && (
                                            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteClick(u)}>
                                                Delete
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>

                {/* Create/Edit Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editingUser ? 'Edit User' : 'Create New User'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control value={username} onChange={e => setUsername(e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password {editingUser && '(Leave blank to keep current)'}</Form.Label>
                                    <Form.Control type="password" value={password} onChange={e => setPassword(e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Select value={role} onChange={e => setRole(e.target.value)}>
                                        <option value="user">Regular User</option>
                                        <option value="admin">Admin</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <h5 className="mb-3">Restrict Page Access</h5>
                                <p className="text-muted small mb-2">
                                    Users have access to every page by default. Check a box to <strong>block</strong> that page for this user.
                                </p>
                                <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '5px'}}>
                                    {role === 'admin' ?
                                        <div className="text-muted text-center mt-5">Admins cannot be restricted.</div>
                                    : (
                                        APP_STRUCTURE.map((cat, idx) => (
                                            <div key={idx} className="mb-3">
                                                <Form.Check
                                                    type="checkbox"
                                                    label={<strong>{cat.category}</strong>}
                                                    checked={isCategoryChecked(cat.items)}
                                                    onChange={() => toggleCategory(cat.items)}
                                                />
                                                <div className="ms-4">
                                                    {cat.items.map((item, i) => (
                                                        <Form.Check
                                                            key={i}
                                                            type="checkbox"
                                                            label={item.label}
                                                            checked={getItemPaths(item).every(p => selectedRestricted.includes(p)) && getItemPaths(item).length > 0}
                                                            onChange={() => togglePath(item)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                        <Button variant="primary" onClick={handleSave}>Save Changes</Button>
                    </Modal.Footer>
                </Modal>
            </Container>
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete user <strong>{userToDelete?.Username}</strong>?
                    <br />
                    <span className="text-danger small">This action cannot be undone.</span>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Delete User
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ManageUsers;
